import {
  contrast,
  hexToOklch,
  isDark,
  mix,
  normalizeHex,
  oklchToHex,
  over,
  parseHex,
  readableOn,
  tint,
} from "./color";

/**
 * What the site falls back to before an organiser has chosen anything.
 *
 * ⚠️ RANGÉS ICI, PAS DANS `useTheme` : le catalogue les déclare aussi (le `fallback` des
 * deux réglages, que la pastille du panneau peint), et `useTheme` passe par
 * `settings/context`, qui lit le catalogue. Une seule source, sans boucle d'imports.
 */
export const DEFAULT_BRAND = "#0f4c81";
export const DEFAULT_PAGE = "#fbfbfa";

/**
 * THE WHOLE PALETTE, FROM TWO ANSWERS.
 *
 * The organiser picks a brand colour and a page colour. Everything else —
 * surfaces, borders, three levels of text, four brand tints, the colour of text
 * ON the brand — is derived, and derived AGAINST MEASURED CONTRAST rather than
 * against a guessed lightness. That is the promise: two settings, and no
 * combination of them can produce an unreadable site.
 *
 * ⚠️ THE BRAND COLOUR IS HONOURED EXACTLY. A client says "our blue is this one",
 * and shifting its lightness to make our own life easier would be answering a
 * different question. What adapts is everything around it: the text laid ON the
 * brand flips between near-white and near-black, whichever reads better, so even
 * a pale yellow button stays legible.
 *
 * ⚠️ CONCRETE VALUES, never `var()`. The prototype's tokens file records why:
 * a `var()` inside an inline override resolves to "undefined", the colour is
 * invalid, and the element turns transparent. Twice.
 */

export type Palette = Record<string, string>;

/** Ratios worth naming: WCAG AA for body text, AAA for the main text colour. */
const BODY = 4.5;
const STRONG = 7;
/** WCAG 1.4.11: what is not text but says something — an outline, a filled mark. */
const MARK = 3;

/**
 * ═══ UNE TEINTE PAR TYPE D'ACTIVITÉ ═══
 *
 * L'étiquette de la régie porte le type ; six étiquettes grises se distinguent par
 * leur seul texte, donc il faut les LIRE pour retrouver son quiz dans une liste.
 *
 * ⚠️ ON FIXE LA TEINTE, PAS LA COULEUR. Une valeur figée serait juste sur le fond
 * clair d'aujourd'hui et illisible sur un événement à fond sombre : c'est exactement
 * la promesse de ce fichier, aucune combinaison des deux réglages ne doit produire
 * quelque chose d'illisible. La teinte est l'identité du type, le reste se calcule
 * contre le fond de l'événement et se mesure.
 *
 * ⚠️ ÉCARTÉES DE `danger` (27), et de lui seul : une pastille rouge au même endroit
 * qu'un état se lit comme un état, et la régie porte déjà du rouge pour ce qui
 * détruit. `ballot` (145) tombe en revanche à côté de `success` (152), et c'est
 * assumé — ces deux verts ne se rencontrent nulle part : `success` marque la bonne
 * réponse DANS un quiz et le verdict d'une question modérée, jamais une pastille de
 * type. Le vert est simplement la place libre entre l'ambre et le cyan.
 */
const ACTIVITY_HUES: Record<string, number> = {
  poll: 250,
  quiz: 300,
  ballot: 145,
  questions: 195,
  cloud: 340,
  leaderboard: 70,
};

/** Assez pour distinguer six teintes, assez peu pour rester une étiquette. */
const CHIP_CHROMA = 0.13;

/**
 * Les pastilles de type : un fond porté par la teinte, et l'encre qui s'y lit.
 *
 * ⚠️ LE FOND NE SE LAVE PAS DANS LA PAGE, ET C'EST UNE CORRECTION. Première version :
 * `mix(page, teinte, 0.16)`, comme les lavis de marque. Sur la page BLANCHE du défaut
 * les six pastilles sortaient distinctes ; sur un événement à page crème, les 84 % de
 * page restants les ramenaient toutes au même beige — mesuré au navigateur, quatre
 * fonds à moins de dix points d'écart. Une pastille dont la couleur dépend surtout de
 * la page ne distingue plus rien, ce qui est sa seule raison d'être.
 *
 * ⚠️ SEULE LA CLARTÉ SUIT LE THÈME. C'est ce qui doit s'adapter — une pastille pâle
 * sur un fond sombre est une tache — et la teinte, elle, est l'identité du type.
 *
 * ⚠️ L'ENCRE SE MESURE CONTRE SON PROPRE FOND, pas contre la page. La pastille porte
 * le sien : c'est lui qu'elle doit battre, et lui seul.
 */
function chips(dark: boolean, hues: Record<string, number>, prefix: string): Palette {
  const out: Palette = {};
  for (const [name, h] of Object.entries(hues)) {
    const soft = oklchToHex({
      l: dark ? 0.3 : 0.91,
      /* Assez de chroma pour que la teinte se VOIE : à 0,055 les six pastilles se
         lisaient encore comme six gris légèrement teintés. */
      c: dark ? 0.085 : 0.08,
      h,
    });
    out[`${prefix}${name}-soft`] = soft;
    out[`${prefix}${name}`] = readableOn(soft, h, CHIP_CHROMA, BODY);
  }
  return out;
}

/**
 * ═══ LA SEULE PASTILLE D'ÉTAT QUI PORTE UNE COULEUR ═══
 *
 * ⚠️ UNE SEULE, ET C'EST LE POINT. Préparée, close, terminée sont des états au repos :
 * colorées elles aussi, les quatre pastilles d'une liste de dix activités feraient un
 * confetti où plus rien ne ressort. « Ouverte » veut dire « celle-ci accepte des
 * réponses en ce moment », le serveur garantit qu'il n'y en a jamais deux, donc la
 * tache verte de la liste désigne exactement l'activité en cours.
 *
 * Le vert par convention de régie — un voyant qui dit « à l'antenne ».
 */
const LIVE_HUE = { live: 150 };

/**
 * ═══ LE ROUGE, ET LE ROUGE ÉCRIT ═══
 *
 * ⚠️ `--gk-danger` RESTE FIGÉ (`tokens.css`), et c'est voulu : il sert de FOND à des
 * pastilles en texte presque blanc (« EN DIRECT », le message de scène, le rond d'une
 * alerte). Le recalculer contre un fond sombre l'éclaircirait, et c'est le blanc posé
 * dessus qui deviendrait illisible.
 *
 * ⚠️ LE ROUGE QU'ON LIT EST UN AUTRE JETON, `--gk-danger-text`, comme `--gk-brand-600`
 * est la marque écrite. Il se mesure contre le pire des fonds où il se pose. Avant lui,
 * « Se déconnecter » était écrit dans le rouge figé sur la surface vert pâle d'un
 * événement : 4,10:1 pour un texte de 13 px (bug #253).
 */
const DANGER_HUE = 27;
const DANGER_CHROMA = 0.17;

/**
 * ═══ LE FOND EN DÉGRADÉ ═══
 *
 * Trois styles pour deux couleurs : la couleur du fond de page, et une seconde.
 *
 *   `vertical`  la première en haut, qui fond vers la seconde en bas ;
 *   `diagonal`  de haut-gauche vers bas-droite ;
 *   `halos`     la première en fond uni, la seconde en deux lueurs dans les coins.
 *
 * ⚠️ LE DÉGRADÉ N'EST QU'UN FOND, `--gk-page` RESTE UNE COULEUR. Tout le reste de la
 * palette se calcule contre une teinte unie — les cartes, les lavis, les pastilles —,
 * et c'est `--gk-page-bg` qui porte le dégradé, peint sur `.site` seul.
 *
 * ⚠️ MAIS LE TEXTE SE MESURE CONTRE LES DEUX. Un titre posé sur le fond passe sur l'une
 * et l'autre teinte selon l'endroit où il tombe : la seconde entre dans le calcul du
 * « pire fond », comme la surface y entre déjà.
 *
 * ⚠️ ET DEUX COULEURS DE BORDS OPPOSÉS NE SE DÉGRADENT PAS. Une claire et une sombre :
 * aucun texte ne se lit sur les deux, l'un des deux bouts serait illisible, quoi qu'on
 * calcule. C'est la promesse de ce fichier — aucun réglage ne produit un site
 * illisible —, donc le fond reste uni, et le panneau le dit (`gradientRefusal`).
 */
export type GradientStyle = "vertical" | "diagonal" | "halos";
export const GRADIENT_STYLES: readonly GradientStyle[] = ["vertical", "diagonal", "halos"];
export type Gradient = { style: string; color: string };

/** Pourquoi un dégradé demandé ne s'applique pas, ou `null` s'il s'applique. */
export function gradientRefusal(
  page: string,
  gradient: Gradient | null | undefined,
): "no-color" | "opposite" | null {
  if (!gradient) return null;
  const second = normalizeHex(gradient.color ?? "");
  const first = normalizeHex(page);
  if (!second || !first) return "no-color";
  return isDark(first) === isDark(second) ? null : "opposite";
}

/** La seconde couleur quand le dégradé s'applique, `null` sinon. */
function secondOf(page: string, gradient: Gradient | null | undefined): string | null {
  if (!gradient || gradientRefusal(page, gradient)) return null;
  return normalizeHex(gradient.color);
}

/** Le style lu, ramené à un style connu : une valeur inconnue prend les halos. */
function styleOf(value: string): GradientStyle {
  return (GRADIENT_STYLES as readonly string[]).includes(value)
    ? (value as GradientStyle)
    : "halos";
}

/**
 * LE FOND DE LA PAGE, uni ou en dégradé — une valeur CSS concrète, jamais `var()`.
 *
 * ⚠️ LES HALOS SE FONDENT VERS LA MÊME TEINTE, TRANSPARENTE, pas vers `transparent` :
 * c'est un noir transparent, et le fondu passait par un gris sale autour de chaque halo.
 */
/* Une couleur en `rgba()`, pour les couches qui laissent voir ce qu'il y a dessous. */
const rgba = (hex: string, alpha: number) => {
  const [r, g, b] = parseHex(hex).map((v) => Math.round(v * 255));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * ═══ L'IMAGE DE FOND, SOUS UN VOILE ═══
 *
 * L'image passe SOUS la couleur du fond — ou sous son dégradé —, posée en transparence :
 * c'est le voile. Il est ce qui rend la promesse tenable : le texte ne se mesure pas
 * contre une photo inconnue, il se mesure contre le pire que le voile laisse passer, un
 * pixel noir sous un thème clair, blanc sous un thème sombre (`imageGrounds`).
 *
 * Trois voiles, pas un curseur : léger, moyen, fort. En dessous de 60 %, un pixel noir
 * sous le voile d'une page blanche ne laisse plus tenir 7:1 au texte principal ; le
 * panneau le dirait, mais mieux vaut ne pas proposer ce qui ne tient pas.
 */
export const VEILS = { light: 0.6, medium: 0.75, strong: 0.9 } as const;
export type Veil = keyof typeof VEILS;
export type PageImage = { url: string; veil: number };

/** Le voile lu, ramené à un voile connu : une valeur inconnue prend le moyen. */
export function veilOf(value: string): number {
  return VEILS[value as Veil] ?? VEILS.medium;
}

/**
 * LES PIRES FONDS QUE LAISSE PASSER LE VOILE : chaque couleur du fond, posée en
 * transparence sur le pixel le plus défavorable au texte.
 */
function imageGrounds(
  colors: ReadonlyArray<string>,
  image: PageImage | null | undefined,
  dark: boolean,
): string[] {
  if (!image) return [];
  const worstPixel = dark ? "#ffffff" : "#000000";
  return colors.map((color) => over(color, image.veil, worstPixel));
}

export function pageBackground(
  page: string,
  gradient: Gradient | null | undefined,
  image?: PageImage | null,
): string {
  const second = secondOf(page, gradient);
  /* Sans image, des couleurs pleines ; avec, les mêmes en transparence, sur l'image. */
  const alpha = image ? image.veil : 1;
  const paint = (hex: string) => (image ? rgba(hex, alpha) : hex);
  const base = image ? `linear-gradient(${paint(page)}, ${paint(page)})` : page;
  const under = image ? `, url(${JSON.stringify(image.url)}) center / cover no-repeat, ${page}` : "";

  if (!second || !gradient) return image ? `${base}${under}` : page;
  const style = styleOf(gradient.style);
  if (style === "vertical") {
    return `linear-gradient(180deg, ${paint(page)} 0%, ${paint(second)} 100%)${under}`;
  }
  if (style === "diagonal") {
    return `linear-gradient(135deg, ${paint(page)} 0%, ${paint(second)} 100%)${under}`;
  }
  const clear = rgba(second, 0);
  return (
    `radial-gradient(ellipse 70% 85% at 0% 0%, ${paint(second)} 0%, ${clear} 70%), ` +
    `radial-gradient(ellipse 60% 75% at 100% 100%, ${paint(second)} 0%, ${clear} 65%), ` +
    base +
    under
  );
}

export function palette(
  brand: string,
  page: string,
  gradient?: Gradient | null,
  image?: PageImage | null,
): Palette {
  const dark = isDark(page);
  /* La seconde couleur du fond, quand il y a un dégradé qui s'applique. */
  const second = secondOf(page, gradient);
  const { h: brandHue, c: brandChroma } = hexToOklch(brand);
  const { l: pageL, c: pageC, h: pageH } = hexToOklch(page);

  // Cards sit ON the page: lighter on a light ground, lighter still on a dark one
  // — a dark theme lifts its surfaces rather than digging further down.
  /**
   * ⚠️ SUR UN THÈME CLAIR, UNE CARTE RESTE PRESQUE BLANCHE, quelle que soit la page.
   * Elle suivait la clarté de la page, à un pas près : juste pour une page presque
   * blanche, faux pour une page soutenue. Sur un fond `#3CBBFF`, les cartes, la barre
   * du haut et les réponses sortaient d'un bleu-gris terne — plus sombres que le bas
   * d'un dégradé vers le blanc, l'inverse d'une carte posée sur un fond. Le plancher
   * de clarté les ramène au blanc, la teinte de la page ne fait plus que les teinter.
   * Pour une page déjà presque blanche, rien ne change : `pageL + 0.015` passe devant.
   */
  const surface = dark
    ? oklchToHex({ l: Math.min(0.99, pageL + 0.06), c: pageC, h: pageH })
    : oklchToHex({
        l: Math.min(1, Math.max(0.975, pageL + 0.015)),
        c: Math.min(0.02, pageC * 0.4),
        h: pageH,
      });
  /* Le second fond (pistes, survols) se règle sur la carte, pas sur la page : sur une
     page soutenue, il en reprenait toute la saturation. */
  const surface2 = dark
    ? oklchToHex({ l: Math.min(0.99, pageL + 0.11), c: pageC, h: pageH })
    : oklchToHex({
        /* Même pas qu'avant sous une page presque blanche (carte 1,000 → 0,965,
           page 0,985 − 0,02 = 0,965) ; sous une page soutenue, un pas sous la carte. */
        l: Math.max(0, hexToOklch(surface).l - 0.035),
        c: Math.min(0.03, pageC),
        h: pageH,
      });

  // Text is measured against the WORST of the two grounds it can land on, never
  // against the friendlier one.
  const worstOfTwo =
    contrast("#000000", page) < contrast("#000000", surface) ? page : surface;
  /* Et contre la seconde couleur du dégradé, et ce que le voile d'une image laisse
     passer, quand ils sont pires : le plus sombre sur un thème clair, le plus clair sur
     un thème sombre. */
  const colors = second ? [page, second] : [page];
  const extras = [...(second ? [second] : []), ...imageGrounds(colors, image, dark)];
  const worst = extras.reduce(
    (current, ground) =>
      dark
        ? contrast("#ffffff", ground) < contrast("#ffffff", current) ? ground : current
        : contrast("#000000", ground) < contrast("#000000", current) ? ground : current,
    worstOfTwo,
  );
  const grounds = [...colors, ...imageGrounds(colors, image, dark)];
  const textChroma = Math.min(0.045, brandChroma);

  // The soft red ground (a hover, an error box): the tokens-file pink on a light
  // theme, a dark red just above the surface on a dark one — pale pink there would
  // be a light patch under light text. Not `mix(surface, red)`: the mix carries the
  // surface's hue half-way, and on a navy event the "red" wash came out purple.
  const dangerSoft = dark
    ? oklchToHex({
        l: Math.min(0.99, hexToOklch(surface).l + 0.05),
        c: 0.05,
        h: DANGER_HUE,
      })
    : oklchToHex({ l: 0.96, c: 0.03, h: DANGER_HUE });
  // Red text lands on the page, on a surface and on its own soft ground: it is
  // measured against whichever of them is closest to the ink it needs.
  const ink = dark ? "#ffffff" : "#000000";
  const dangerWorst = [...grounds, surface, dangerSoft].reduce((a, b) =>
    contrast(ink, a) <= contrast(ink, b) ? a : b,
  );

  /*
   * ═══ LA MARQUE QUI DIT « RETENU » ═══
   *
   * ⚠️ `--gk-brand-500` N'EST MESURÉ CONTRE RIEN, et c'est lui qui dessinait le contour
   * d'une réponse retenue et remplissait sa case. Sur l'événement 706 (marque `#91bc8f`,
   * page `#88ff99`) : contour à 1,72:1 de la page, case à 1,67:1 de sa carte — la réponse
   * choisie se fondait dans la page (bug #264).
   *
   * La marque reste celle de l'organisateur QUAND ELLE PASSE : 3:1 contre chacun des
   * fonds où l'état se pose — la page autour de la carte, la surface des cartes voisines,
   * le lavis de la carte retenue. Sinon, sa teinte, éloignée de ces fonds jusqu'au 3:1,
   * comme `--gk-brand-600` l'est pour le texte.
   */
  /**
   * ⚠️ LES LAVIS DE MARQUE SE TIRENT DE LA CARTE SUR UN THÈME CLAIR, PAS DE LA PAGE. Ils
   * colorent ce qui est posé SUR une carte — la réponse retenue, l'étape en cours, la
   * pastille d'une coche. Tirés de la page, ils en gardaient 94 % : sur une page bleue et
   * une marque verte, la réponse choisie et l'étape active sortaient BLEUES. De la carte
   * presque blanche, ils gardent la teinte de la marque. Sous une page presque blanche,
   * carte et page se confondent : rien ne change.
   */
  const washBase = dark ? page : surface;
  /* Et la TEINTE est celle de la marque : voir `tint`. */
  const brand50 = tint(washBase, brand, dark ? 0.1 : 0.06);
  const markWorst = [...grounds, surface, brand50].reduce((a, b) =>
    contrast(ink, a) <= contrast(ink, b) ? a : b,
  );
  const mark =
    contrast(brand, markWorst) >= MARK
      ? brand
      : readableOn(markWorst, brandHue, brandChroma, MARK);

  return {
    "--gk-page": page,
    "--gk-page-bg": pageBackground(page, gradient, image),
    "--gk-surface": surface,
    "--gk-surface-2": surface2,
    "--gk-border": mix(
      surface,
      dark ? "#ffffff" : "#000000",
      dark ? 0.14 : 0.1,
    ),
    "--gk-border-strong": mix(
      surface,
      dark ? "#ffffff" : "#000000",
      dark ? 0.24 : 0.18,
    ),

    // A hint of the brand hue in the text: it is what clients actually do, and
    // pure grey next to a strong brand reads as unfinished.
    "--gk-text": readableOn(worst, brandHue, textChroma, STRONG),
    "--gk-text-2": readableOn(worst, brandHue, textChroma * 0.7, BODY + 0.3),
    "--gk-text-3": readableOn(worst, brandHue, textChroma * 0.5, BODY),

    "--gk-brand-500": brand,
    // Whichever of near-white and near-black reads better ON the brand. A fixed
    // white gave 2.3:1 on a navy-blue event — the defect this replaces.
    "--gk-on-brand":
      contrast("#ffffff", brand) >= contrast("#141414", brand)
        ? "#ffffff"
        : "#141414",
    // The brand as TEXT, on a surface: it has to clear body contrast, and the
    // brand itself usually does not.
    "--gk-brand-600": readableOn(surface, brandHue, brandChroma, BODY),
    "--gk-brand-700": readableOn(surface, brandHue, brandChroma, STRONG),
    // The brand as the SELECTED state — outline and filled mark — and what sits on it.
    // Also the focus ring (bug #410): `--gk-brand-500` is measured against nothing.
    "--gk-brand-mark": mark,
    "--gk-on-mark":
      contrast("#ffffff", mark) >= contrast("#141414", mark)
        ? "#ffffff"
        : "#141414",
    // Washes, for chips and soft backgrounds: the brand pulled towards the page.
    "--gk-brand-50": brand50,
    "--gk-brand-100": tint(washBase, brand, dark ? 0.18 : 0.12),
    "--gk-brand-200": tint(washBase, brand, dark ? 0.3 : 0.24),

    "--gk-danger-soft": dangerSoft,
    "--gk-danger-text": readableOn(dangerWorst, DANGER_HUE, DANGER_CHROMA, BODY),

    ...chips(dark, ACTIVITY_HUES, '--gk-act-'),
    ...chips(dark, LIVE_HUE, '--gk-'),
  };
}

/** Les types que les pastilles savent colorer, pour qui doit les tenir à jour. */
export const CHIP_TYPES = Object.keys(ACTIVITY_HUES);

/** What the panel shows the organiser: the promise, measured. */
export function contrastReport(
  brand: string,
  page: string,
  gradient?: Gradient | null,
  image?: PageImage | null,
) {
  const p = palette(brand, page, gradient, image);
  const second = secondOf(page, gradient);
  const colors = second ? [page, second] : [page];
  const grounds = [...colors, ...imageGrounds(colors, image, isDark(page))];
  return {
    /* Sur le fond le moins favorable : les deux couleurs d'un dégradé, et le pire que
       le voile d'une image laisse passer. */
    text: Math.min(...grounds.map((ground) => contrast(p["--gk-text"]!, ground))),
    onBrand: contrast(p["--gk-on-brand"]!, brand),
    brandText: contrast(p["--gk-brand-600"]!, p["--gk-surface"]!),
  };
}
